import {
	parseAdIdFromDeepLinkParam,
	parseAdIdFromPathname,
} from "@/app/utils/ad-share";

export type ClientBootstrapRoute = {
  /** Subset of app pages that can be chosen from cold URL (cast to PageType in useAppState). */
  currentPage: "home" | "order-confirmation" | "ad-detail" | "received-offers";
  lastOrderNumber: string;
  selectedAdId: string | null;
  /** When opening from email (?notify_link=/offers/received?highlight=...) */
  selectedOfferId: string | null;
};

/**
 * Read URL once on client mount to avoid flashing Home before Stripe return or ?ad= handling.
 */
export function getClientBootstrapRoute(): ClientBootstrapRoute {
  const empty = (): ClientBootstrapRoute => ({
    currentPage: "home",
    lastOrderNumber: "",
    selectedAdId: null,
    selectedOfferId: null,
  });
  if (typeof window === "undefined") {
    return empty();
  }
	try {
		const path = (window.location.pathname || "").replace(/\/+$/, "") || "/";
		const adFromPath = parseAdIdFromPathname(path);
		if (adFromPath) {
			return {
				currentPage: "ad-detail",
				lastOrderNumber: "",
				selectedAdId: adFromPath,
				selectedOfferId: null,
			};
		}
		const p = new URLSearchParams(window.location.search);
		if (p.get("stripe_return") === "1") {
      const oid = p.get("order_id")?.trim();
      if (oid) {
        return { currentPage: "order-confirmation", lastOrderNumber: oid, selectedAdId: null, selectedOfferId: null };
      }
    }
    const aid = parseAdIdFromDeepLinkParam(p.get("ad"));
    if (aid) {
      return { currentPage: "ad-detail", lastOrderNumber: "", selectedAdId: aid, selectedOfferId: null };
    }
    const notifyRaw = p.get("notify_link")?.trim();
    if (notifyRaw) {
      try {
        const decoded = decodeURIComponent(notifyRaw);
        const pathPart = decoded.split("?")[0].replace(/^\//, "").toLowerCase();
        const qIdx = decoded.indexOf("?");
        const search = qIdx >= 0 ? decoded.slice(qIdx) : "";
        const params = search ? new URLSearchParams(search) : null;
        const highlight = params?.get("highlight")?.trim() || null;
        if (pathPart === "offers/received") {
          return {
            currentPage: "received-offers",
            lastOrderNumber: "",
            selectedAdId: null,
            selectedOfferId: highlight,
          };
        }
      } catch {
        /* ignore malformed notify_link */
      }
    }
  } catch {
    /* ignore */
  }
  return empty();
}

/** True while URL indicates Stripe payment return (used to suppress cart hydration race). */
export function shouldSuppressCartForStripeReturnUrl(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const p = new URLSearchParams(window.location.search);
    return p.get("stripe_return") === "1" && !!p.get("order_id")?.trim();
  } catch {
    return false;
  }
}

/** True when `?ad=` or `/ad/<id>` is present so the deep-link effect must not push duplicate history. */
export function hasAdDeepLinkInUrl(): boolean {
	if (typeof window === "undefined") return false;
	try {
		const path = (window.location.pathname || "").replace(/\/+$/, "") || "/";
		if (parseAdIdFromPathname(path)) return true;
		return !!parseAdIdFromDeepLinkParam(
			new URLSearchParams(window.location.search).get("ad"),
		);
	} catch {
		return false;
	}
}

/** True when ?notify_link= is present (offer email deep link). */
export function hasNotifyLinkInUrl(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return !!new URLSearchParams(window.location.search).get("notify_link")?.trim();
  } catch {
    return false;
  }
}

/** Removes notify_link from the URL after the SPA consumed the offer deep link. */
export function removeNotifyLinkFromAddressBar(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has("notify_link")) return;
  url.searchParams.delete("notify_link");
  const q = url.searchParams.toString();
  const next = url.pathname + (q ? `?${q}` : "") + url.hash;
  window.history.replaceState(window.history.state, "", next);
}
